/**
 * Helpers de tiempo — fuente única de verdad.
 * Reemplaza las copias duplicadas en HomeView / CargaSemanal / SemanaDetailView / ResumenView.
 *
 * Convención: el tiempo se almacena y se pasa al backend como string "HH:MM".
 * El formato decimal es SOLO para visualización (preferencia us_ts_TimeTipo).
 */

export function padToTwoDigits(num) {
  return num.toString().padStart(2, '0')
}

/** "HH:MM" -> minutos (int). Tolerante a vacío / formato inválido. */
export function timeToMinutes(time) {
  if (!time) return 0
  const parts = String(time).split(':')
  const h = Number(parts[0]) || 0
  const m = Number(parts[1]) || 0
  return h * 60 + m
}

/** minutos -> "HH:MM" (sin tope de 24h; nunca negativo). */
export function minutesToTime(totalMinutes) {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0))
  const hours = Math.floor(mins / 60)
  const minutes = mins % 60
  return `${padToTwoDigits(hours)}:${padToTwoDigits(minutes)}`
}

/** "HH:MM" -> horas decimales (float). "01:30" -> 1.5 */
export function timeToDecimal(timeStr) {
  if (!timeStr || timeStr === '') return 0
  const [hours, minutes] = String(timeStr).split(':').map(Number)
  if (isNaN(hours) || isNaN(minutes)) return 0
  return hours + minutes / 60
}

/** horas decimales -> "HH:MM". 1.5 -> "01:30". Maneja redondeo a 60. */
export function decimalToTime(decimal) {
  const dec = parseFloat(decimal)
  if (!dec || isNaN(dec)) return '00:00'
  const hours = Math.floor(dec)
  const minutes = Math.round((dec - hours) * 60)
  if (minutes === 60) return minutesToTime((hours + 1) * 60)
  return `${padToTwoDigits(hours)}:${padToTwoDigits(minutes)}`
}

/**
 * Normaliza cualquier entrada del usuario a "HH:MM".
 * Acepta: "1:30", "1.5", "1,5", "130" (HMM), "0930" (HHMM), "8" (horas), "90m" / "90min".
 * Vacío -> "00:00".
 */
export function parseTimeInput(raw) {
  if (raw == null) return '00:00'
  const s = String(raw).trim().toLowerCase()
  if (s === '') return '00:00'

  // Minutos explícitos: "90m", "90 min"
  const minMatch = s.match(/^(\d+)\s*m(in)?$/)
  if (minMatch) return minutesToTime(parseInt(minMatch[1], 10))

  // Formato HH:MM
  if (s.includes(':')) {
    const [h, m] = s.split(':')
    return minutesToTime((parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0))
  }

  // Decimal: "1.5" o "1,5"
  if (s.includes('.') || s.includes(',')) {
    const dec = parseFloat(s.replace(',', '.'))
    return isNaN(dec) ? '00:00' : decimalToTime(dec)
  }

  // Solo dígitos
  if (/^\d+$/.test(s)) {
    if (s.length <= 2) return minutesToTime(parseInt(s, 10) * 60) // "8" -> 08:00
    const mm = parseInt(s.slice(-2), 10) // "130" -> 01:30 / "0930" -> 09:30
    const hh = parseInt(s.slice(0, -2), 10)
    return minutesToTime(hh * 60 + mm)
  }

  return '00:00'
}

/**
 * Formatea para mostrar. decimal=true -> "1.50"; decimal=false -> "HH:MM".
 * Devuelve '' para valores vacíos / "00:00" (igual que el front actual).
 */
export function formatTime(value, decimal = false) {
  if (value == null || value === '' || value === '0' || value === '00:00') return ''
  if (decimal) {
    const num = typeof value === 'number' ? value : timeToDecimal(value)
    return num > 0 ? num.toFixed(2) : ''
  }
  return value !== '00:00' ? String(value) : ''
}

/**
 * Segundos de un cronómetro -> "HH:MM", redondeado a los 5 minutos más
 * cercanos (5/10/15/20...). Nunca redondea a 0: por debajo de 2:30 min
 * (mitad del primer intervalo) igual carga 5 min, que es el mínimo.
 */
export function secondsToRoundedTime(totalSeconds) {
  const minutos = (Number(totalSeconds) || 0) / 60
  const redondeado = Math.round(minutos / 5) * 5
  return minutesToTime(redondeado || 5)
}

/** Suma una lista de valores "HH:MM" y devuelve "HH:MM". */
export function sumTimes(times) {
  const total = (times || []).reduce((acc, t) => acc + timeToMinutes(t), 0)
  return minutesToTime(total)
}
